import { invokeFunction } from "@/lib/functions";
import { supabase } from "@/lib/supabase";

export interface WorkspaceBillingSummary {
  containerBilling: {
    id: string;
    container_id: string;
    customer_id: string | null;
    subscription_id: string | null;
    created_at: string;
    updated_at: string;
  } | null;
  customer: {
    hyperline_customer_id: string | null;
    status: string | null;
    email: string | null;
    updated_at: string | null;
  } | null;
  subscription: {
    hyperline_subscription_id: string | null;
    status: string | null;
    subscription_status: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    updated_at: string | null;
  } | null;
}

export interface HyperlineJobSummary {
  id: string;
  workspace_id: string;
  workspace_name: string | null;
  user_email: string | null;
  status: string;
  request_id: number | null;
  customer_id: string | null;
  subscription_id: string | null;
  last_error: string | null;
  last_attempt_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceSubscriptionPhase {
  id: string;
  name: string;
  status: string | null;
  type: string | null;
  starts_at: string | null;
  ends_at: string | null;
}

export interface WorkspaceCreditState {
  workspace_id: string;
  balance_remaining: number;
  balance_unit: string;
  threshold_warning: number;
  threshold_sync: number;
  threshold_hard_stop: number;
  threshold_unit: string;
  conversion_per_unit: number | null;
  is_ai_blocked: boolean;
  last_reconciled_at: string | null;
  last_alert_sent_at: string | null;
  updated_at: string;
  notes?: string | null;
}

export interface WorkspaceCreditUsageSnapshot {
  remaining: number;
  unit: string;
  total?: number | null;
  used?: number | null;
  lastUpdatedAt?: string | null;
}

export interface RefreshWorkspaceCreditsResponse {
  creditState: WorkspaceCreditState;
  usage: WorkspaceCreditUsageSnapshot;
}

export interface UpdateWorkspaceCreditStateParams {
  workspaceId: string;
  threshold_warning: number;
  threshold_sync: number;
  threshold_hard_stop: number;
  threshold_unit: string;
  balance_unit: string;
  conversion_per_unit: number | null;
  is_ai_blocked: boolean;
}

export async function getWorkspaceBillingSummary(
  workspaceId: string,
): Promise<WorkspaceBillingSummary> {
  const { data, error } = await (supabase.from as any)("container_billing")
    .select(
      `
        id,
        container_id,
        customer_id,
        subscription_id,
        created_at,
        updated_at,
        customer:billing_customers(hyperline_customer_id,status,email,updated_at),
        subscription:billing_subscriptions(hyperline_subscription_id,status,subscription_status,current_period_start,current_period_end,updated_at)
      `,
    )
    .eq("container_id", workspaceId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return {
      containerBilling: null,
      customer: null,
      subscription: null,
    };
  }

  return {
    containerBilling: {
      id: data.id,
      container_id: data.container_id,
      customer_id: data.customer_id,
      subscription_id: data.subscription_id,
      created_at: data.created_at,
      updated_at: data.updated_at,
    },
    customer: data.customer ?? null,
    subscription: data.subscription ?? null,
  };
}

export async function getLatestHyperlineJob(
  workspaceId: string,
): Promise<HyperlineJobSummary | null> {
  const { data, error } = await (supabase.from as any)("hyperline_jobs")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data ?? null;
}

export async function createHyperlinePortalSession(params: {
  workspaceId: string;
}): Promise<string> {
  const { data, error } = await invokeFunction<{
    portalUrl?: string;
    error?: string;
  }>("hyperline-customer-portal", {
    body: {
      workspaceId: params.workspaceId,
    },
  });

  if (error) {
    throw error;
  }

  if (!data?.portalUrl) {
    throw new Error(data?.error ?? "Failed to create Hyperline customer portal session");
  }

  return data.portalUrl;
}

export async function retryWorkspaceBilling(params: {
  workspaceId: string;
  workspaceName: string;
}): Promise<string> {
  const { data, error } = await invokeFunction<{
    jobId?: string;
    error?: string;
  }>("hyperline-billing-retry", {
    body: {
      workspaceId: params.workspaceId,
      workspaceName: params.workspaceName,
    },
  });

  if (error) {
    throw error;
  }

  if (!data?.jobId) {
    throw new Error(data?.error ?? "Failed to enqueue billing job");
  }

  return data.jobId;
}

export async function getWorkspaceCreditState(
  workspaceId: string,
): Promise<WorkspaceCreditState | null> {
  const { data, error } = await (supabase.from as any)("workspace_credit_state")
    .select(
      `workspace_id,
       balance_remaining,
       balance_unit,
       threshold_warning,
       threshold_sync,
       threshold_hard_stop,
       threshold_unit,
       conversion_per_unit,
       is_ai_blocked,
       last_reconciled_at,
       last_alert_sent_at,
       updated_at`,
    )
    .eq("workspace_id", workspaceId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data ?? null;
}

export async function updateWorkspaceCreditState(
  params: UpdateWorkspaceCreditStateParams,
): Promise<WorkspaceCreditState> {
  const { data, error } = await invokeFunction<{
    success?: boolean;
    data?: WorkspaceCreditState;
    error?: string;
  }>("workspace-credit-settings", {
    body: params,
  });

  if (error) {
    throw error;
  }

  if (!data?.success || !data.data) {
    throw new Error(data?.error ?? "Failed to update credit state");
  }

  return data.data;
}

export async function refreshWorkspaceCredits(
  workspaceId: string,
): Promise<RefreshWorkspaceCreditsResponse> {
  const { data, error } = await invokeFunction<{
    success?: boolean;
    data?: WorkspaceCreditState;
    usage?: WorkspaceCreditUsageSnapshot;
    error?: string;
  }>("hyperline-credit-refresh", {
    body: {
      workspaceId,
    },
  });

  if (error) {
    throw error;
  }

  if (!data?.success || !data.data || !data.usage) {
    throw new Error(data?.error ?? "Failed to refresh workspace credits");
  }

  return {
    creditState: data.data,
    usage: data.usage,
  };
}

export async function getWorkspaceSubscriptionPhase(
  workspaceId: string,
): Promise<WorkspaceSubscriptionPhase | null> {
  if (!workspaceId) {
    return null;
  }

  const { data, error } = await invokeFunction<{
    success?: boolean;
    data?: WorkspaceSubscriptionPhase;
    error?: string;
  }>("hyperline-subscription-phase", {
    body: { workspaceId },
  });

  if (error) {
    throw error;
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data?.data ?? null;
}
